/**
 * Build canonical match history payload from a completed Competition draw fixture.
 */

import { buildCanonicalMatchPayload } from "../matchHistory/buildMatchHistoryPayload";
import {
  buildFixtureSideSnapshots,
  fixtureHistoryIdempotencyKey,
} from "./playerCareer";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";

/**
 * @param {object} input
 * @param {object} input.competition
 * @param {object} input.draw
 * @param {object} input.fixture
 * @param {string} input.createdBy
 */
export function buildFixtureMatchHistoryInput(input) {
  const { competition, draw, fixture, createdBy } = input;
  if (!fixture?.fixtureId || !draw?.drawId || !competition?.competitionId) {
    throw new Error("Competition, draw, and fixture are required.");
  }
  if (fixture.isBye || fixture.status === "bye") {
    throw new Error("BYE fixtures do not write history.");
  }
  const winnerEntryId = fixture.winnerEntryId;
  if (!winnerEntryId) throw new Error("Winner required to write history.");

  const winnerSide =
    winnerEntryId === fixture.entryAId ? "A" : winnerEntryId === fixture.entryBId ? "B" : null;
  if (!winnerSide) throw new Error("Winner must be a fixture participant.");

  const { teamA, teamB, snapshots } = buildFixtureSideSnapshots(competition, fixture);
  const scoreA = fixture.scoreA ?? 0;
  const scoreB = fixture.scoreB ?? 0;
  const sets =
    Array.isArray(fixture.scores) && fixture.scores.length
      ? fixture.scores
      : [
          {
            scoreA,
            scoreB,
            winner: winnerSide,
          },
        ];

  const completedAtMs = fixture.completedAtMs || Date.now();
  const durationSec =
    fixture.startedAtMs && fixture.completedAtMs && fixture.completedAtMs > fixture.startedAtMs
      ? Math.round((fixture.completedAtMs - fixture.startedAtMs) / 1000)
      : null;

  const idempotencyKey = fixtureHistoryIdempotencyKey(
    competition.competitionId,
    draw.drawId,
    fixture.fixtureId
  );

  const payloadInput = {
    sessionRoomId: null,
    teamA,
    teamB,
    sets,
    winnerSide,
    createdBy,
    completedAtMs,
    durationSec,
    scoringFormat: competition.settings?.defaultMatchFormat?.name || "tournament",
    participantSnapshots: snapshots,
    competitionId: competition.competitionId,
    clubId: null,
    source: "competition_fixture",
    idempotencyKey,
    sportId: normalizeSportId(competition.sportId || competition.sport || DEFAULT_SPORT),
  };

  const built = buildCanonicalMatchPayload(payloadInput);
  // Annotate participants with round for career title/runner-up detection via score cards
  built.participants = built.participants.map((p) => ({
    ...p,
    // denormalized onto card via presentation if we put round on record.result is enough
  }));
  built.record.result = `${
    winnerSide === "A" ? teamA.name : teamB.name
  } won (${built.record.sets.map((s) => `${s.scoreA}-${s.scoreB}`).join(", ")}) · ${
    fixture.roundName || "Match"
  }`;

  return {
    ...payloadInput,
    roundName: fixture.roundName || null,
    eventId: draw.eventId || null,
    drawId: draw.drawId,
    fixtureId: fixture.fixtureId,
    matchId: built.record.matchId,
  };
}
